import axios, { type AxiosRequestConfig } from "axios";
import { getCsrfToken, getWsId, useAuthStore } from "@/auth/store";

/**
 * Single axios instance behind every API call (orval mutator included).
 *
 * - baseURL is the page origin: OpenAPI paths already include /api/v1/...,
 *   and in dev the API server proxies non-/api routes to Vite, so the app is
 *   always browsed through the API origin.
 * - `filter` (and any object param) is JSON-stringified — backend FilterArgs
 *   uses @JsonTransform().
 */
export const api = axios.create({
    baseURL: location.origin,
    withCredentials: true,
    paramsSerializer: {
        serialize: (params) => {
            const search = new URLSearchParams();
            for (const [key, value] of Object.entries(params)) {
                if (value === undefined || value === null) continue;
                search.append(
                    key,
                    typeof value === "object" ? JSON.stringify(value) : String(value),
                );
            }
            return search.toString();
        },
    },
});

// Uploads are NOT ws-exempt: a workspace logo's staged upload needs `x-ws-id`
// for the backend's `resolveTarget` to resolve `requireWS(auth,
// Workspace__Write)`. An avatar upload's `resolveTarget` ignores wsId
// entirely, so sending the header there is harmless.
/** Paths that never receive workspace context. */
const WS_EXEMPT = /^\/api\/v1\/(auth|account)(\/|$)/;
// ⚠ The one `/account/*` route that DOES need workspace context, and it must win over
// WS_EXEMPT above. "What may I do" is only answerable relative to a membership, so the
// backend declares it `@Protected(true, true)`; without the header it 403s with "requires
// an workspace context" and `usePermission()` reports that the user holds nothing — every
// button in the app disappears, with no error anywhere to explain why.
const WS_REQUIRED = /^\/api\/v1\/account\/permissions(\?|$)/;
/** Paths whose 401 means "bad credentials", not "session expired". */
const SESSION_EXEMPT = /^\/api\/v1\/auth\/(login|2fa)(\/|$)/;

api.interceptors.request.use((config) => {
    // CSRF is enforced on every protected route, GETs included (the Swagger doc's
    // claim that it's writes-only is wrong) — always attach it, not just on writes.
    const csrfToken = getCsrfToken();
    if (csrfToken) config.headers.set("X-CSRF-Token", csrfToken);
    const wsId = getWsId();
    const url = config.url ?? "";
    if (wsId && (!WS_EXEMPT.test(url) || WS_REQUIRED.test(url))) {
        config.headers.set("x-ws-id", wsId);
    }
    return config;
});

api.interceptors.response.use(undefined, (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? "";
    // Only a 401 on a call we believed was authenticated (we already hold a
    // csrfToken from a prior login) counts as "session expired" — a 401 from a
    // page that speculatively checks auth state while genuinely logged out
    // (e.g. the invite-accept deep link) must not pop the un-closable
    // "Session expired" modal for a visitor who was never signed in.
    if (status === 401 && !SESSION_EXEMPT.test(url) && useAuthStore.getState().csrfToken) {
        useAuthStore.getState().setSessionExpired(true);
    }
    return Promise.reject(error);
});

/** Orval mutator: every generated hook routes through the shared instance. */
export const apiMutator = <T>(config: AxiosRequestConfig): Promise<T> =>
    api.request<T>(config).then((response) => response.data);

export default apiMutator;
